import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import billingApi, { Plan } from '@/services/billingApi';
import { useSubscription } from '@/hooks/useSubscription';

type BillingCycle = 'monthly' | 'yearly';

const featureBullets = (plan: Plan): string[] => {
  const e = plan.entitlements;
  const bullets: string[] = [];
  bullets.push(
    e.videoProcessingHoursPerMonth < 0
      ? 'Unlimited video processing'
      : `${e.videoProcessingHoursPerMonth} hrs of video processing / month`
  );
  bullets.push(e.clipsPerMonth < 0 ? 'Unlimited AI short clips' : `Up to ${e.clipsPerMonth} AI short clips`);
  if (e.thumbnailGenerator) bullets.push('AI thumbnail & social graphics generator');
  if (e.translationLanguagesTier) bullets.push(`AI translation in ${e.translationLanguagesTier} languages`);
  if (e.voiceCloning) bullets.push(e.lipSync ? 'Voice cloning & lip sync' : 'Voice cloning');
  bullets.push(
    e.connectedAccountsLimit < 0 ? 'Unlimited connected accounts' : `${e.connectedAccountsLimit} connected social account${e.connectedAccountsLimit === 1 ? '' : 's'}`
  );
  if (e.schedulingCalendar) bullets.push('Scheduling calendar & drafts');
  if (e.analyticsDashboard) bullets.push('Unified analytics dashboard');
  if (e.clientWorkspaces > 0 || e.clientWorkspaces < 0) {
    bullets.push(e.clientWorkspaces < 0 ? 'Unlimited client workspaces' : `Up to ${e.clientWorkspaces} client workspaces`);
  }
  if (e.apiAccess) bullets.push('API access & private integrations');
  if (e.ssoSaml) bullets.push('SSO, SAML & audit logs');
  if (e.customAiModels) bullets.push('Custom AI models and voices');
  if (e.prioritySupport) bullets.push('Priority support');
  return bullets;
};

const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly');
  const { data: subscription } = useSubscription();

  const { data, isLoading } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: () => billingApi.getPlans(),
    staleTime: 5 * 60 * 1000,
  });

  const plans: Plan[] = data?.data.plans ?? [];

  const checkoutMutation = useMutation({
    mutationFn: ({ plan }: { plan: string }) => billingApi.createCheckoutSession(plan, billingCycle),
    onSuccess: (res) => {
      if (res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      }
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to start checkout');
    },
  });

  const handleSelectPlan = (plan: Plan) => {
    if (plan.isCustomPricing) {
      window.location.href = 'mailto:sales@primeai.com?subject=Enterprise%20plan%20inquiry';
      return;
    }
    checkoutMutation.mutate({ plan: plan.key });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Choose your plan</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Pick a plan that matches how much you publish. Upgrade or cancel anytime.
        </p>
        <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1 text-sm">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`rounded-full px-5 py-2 font-semibold transition-colors ${
              billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`rounded-full px-5 py-2 font-semibold transition-colors ${
              billingCycle === 'yearly' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Annual <span className="ml-1 opacity-90">save 20%</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
          {plans.map((plan) => {
            const isCurrent = subscription?.plan === plan.key;
            const price = billingCycle === 'yearly' ? plan.yearlyPriceUsd : plan.monthlyPriceUsd;
            const displayMonthly = billingCycle === 'yearly' && plan.yearlyPriceUsd ? Math.round(plan.yearlyPriceUsd / 12) : plan.monthlyPriceUsd;

            return (
              <div
                key={plan.key}
                className={`relative flex flex-col p-5 rounded-xl border bg-white dark:bg-gray-800 ${
                  plan.mostPopular
                    ? 'ring-2 ring-blue-600 shadow-lg border-transparent'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {plan.mostPopular && (
                  <span className="absolute -top-3 left-6 rounded-full px-3 py-1 text-xs font-bold text-white bg-blue-600">
                    Most popular
                  </span>
                )}
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{plan.name}</h2>
                <p className="mt-2 min-h-10 text-sm text-gray-500 dark:text-gray-400">{plan.description}</p>

                <div className="mt-6 flex items-end gap-1">
                  {plan.isCustomPricing ? (
                    <span className="text-3xl font-extrabold text-gray-900 dark:text-white">Custom</span>
                  ) : (
                    <>
                      <span className="text-4xl font-extrabold text-gray-900 dark:text-white">${displayMonthly}</span>
                      <span className="pb-1 text-sm text-gray-500 dark:text-gray-400">/mo</span>
                    </>
                  )}
                </div>
                {!plan.isCustomPricing && billingCycle === 'yearly' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Billed annually (${price}/yr)</p>
                )}

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isCurrent || checkoutMutation.isLoading}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 ${
                    plan.mostPopular ? 'bg-blue-600 text-white' : 'border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                  }`}
                >
                  {isCurrent ? 'Current plan' : plan.isCustomPricing ? 'Talk to sales' : `Upgrade to ${plan.name}`}
                </button>

                <ul className="mt-6 space-y-3 text-sm">
                  {featureBullets(plan).map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-blue-600" /> Cancel anytime
        </span>
      </div>
    </div>
  );
};

export default PricingPage;
